import type { IncomingMessage, ServerResponse } from 'http'
import { GetAccessTokenOptions, SessionData } from '@auth0/nextjs-auth0/types'
import { getMembershipIdFromSub, UNAUTHENTICATED_USER_ID } from '../utils'
import { Auth0B2CException, logAuth0Error } from './error'
import { getAuth0Server } from './auth0/auth0.instance'

const MEMBERSHIP_ID_CLAIM = 'https://avios.com/membership_id'

type TokenPayload = {
  sub?: string
  membershipNumber?: string
  [MEMBERSHIP_ID_CLAIM]?: string
}

const getBearerToken = (authorizationHeader?: string): string | undefined => {
  if (!authorizationHeader?.startsWith('Bearer ')) {
    return undefined
  }

  return authorizationHeader.slice(7)
}

const getHeaderValue = (header: string | string[] | undefined): string | undefined => {
  if (Array.isArray(header)) {
    return header[0]
  }

  return header
}

const parseTokenPayload = (token?: string): TokenPayload | undefined => {
  if (!token) {
    return undefined
  }

  if (token.trim().startsWith('{')) {
    return JSON.parse(token) as TokenPayload
  }

  const payloadBase64 = token.split('.')[1]
  if (!payloadBase64) {
    return undefined
  }

  return JSON.parse(Buffer.from(payloadBase64, 'base64url').toString('utf-8')) as TokenPayload
}

const getCentralAuthMemberId = ({
  membershipId,
  membershipNumber,
  sub,
}: {
  membershipId?: string
  membershipNumber?: string
  sub?: string
}): string | undefined => {
  if (membershipId) {
    return membershipId
  }

  if (membershipNumber) {
    return membershipNumber
  }

  if (sub) {
    return sub.replace('auth0|', '')
  }

  return undefined
}

const resolveCentralAuthRequest = (req: IncomingMessage): boolean => {
  const platformType = getHeaderValue(req.headers['x-platform-type'])
  return (
    platformType === 'avios' &&
    process.env.NEXT_PUBLIC_AVIOS_CENTRAL_AUTH?.trim().toLowerCase() === 'true'
  )
}

export const getSession = async (
  req: IncomingMessage,
  res: ServerResponse,
  isCentralAuth = false
): Promise<SessionData | null> => {
  if (isCentralAuth) {
    const token = getBearerToken(req.headers.authorization)

    try {
      const payload = parseTokenPayload(token)

      if (payload?.sub || payload?.[MEMBERSHIP_ID_CLAIM] || payload?.membershipNumber) {
        return {
          user: {
            sub: payload?.sub ?? '',
            membershipNumber: payload?.membershipNumber,
            [MEMBERSHIP_ID_CLAIM]: payload?.[MEMBERSHIP_ID_CLAIM] ?? payload?.membershipNumber,
          },
        } as unknown as SessionData
      }
    } catch {
      // Treat invalid central auth tokens as unauthenticated rather than falling back to Auth0
    }

    return null
  }

  try {
    return getAuth0Server(req).getSession(req)
  } catch (error) {
    if (error instanceof Error) {
      const authError = new Auth0B2CException(error, 'REQUEST_UNAUTHORIZED')
      logAuth0Error('Failed fetching auth session', authError, req, res)
      throw authError
    }
    throw error
  }
}

export const getAccessToken = (
  req: IncomingMessage,
  res: ServerResponse,
  opt?: GetAccessTokenOptions,
  isCentralAuth = false
) => {
  const authHeader = req.headers.authorization
  if (isCentralAuth) {
    const token = authHeader?.startsWith('Bearer ') ? authHeader.slice(7) : authHeader
    return Promise.resolve({ token: token ?? '' })
  }
  try {
    return getAuth0Server(req).getAccessToken(req, res, { ...opt })
  } catch (error) {
    if (error instanceof Error) {
      const authError = new Auth0B2CException(error, 'REQUEST_UNAUTHORIZED')
      logAuth0Error('Failed fetching access token', authError, req, res)
      throw authError
    }
    throw error
  }
}

export const getUserId = async (req: IncomingMessage, res: ServerResponse) => {
  const isCentralAuthRequest = resolveCentralAuthRequest(req)
  // Extract the membership ID from the JWT if the Authorization header is present
  const token = getBearerToken(req.headers.authorization)
  if (token) {
    try {
      const payload = parseTokenPayload(token)

      if (isCentralAuthRequest) {
        const memberId = getCentralAuthMemberId({
          membershipId: payload?.[MEMBERSHIP_ID_CLAIM],
          membershipNumber: payload?.membershipNumber,
          sub: payload?.sub,
        })

        if (memberId) {
          return memberId
        }
      }

      if (payload?.sub) {
        return getMembershipIdFromSub(payload.sub)
      }
    } catch {
      // Fall back to session lookup
    }
  }

  const session = await getSession(req, res, isCentralAuthRequest)
  if (!session?.user) {
    return UNAUTHENTICATED_USER_ID
  }

  if (isCentralAuthRequest) {
    return (
      getCentralAuthMemberId({
        membershipId: session.user[MEMBERSHIP_ID_CLAIM],
        membershipNumber: (session.user as { membershipNumber?: string }).membershipNumber,
        sub: session.user.sub,
      }) ?? UNAUTHENTICATED_USER_ID
    )
  }

  return getMembershipIdFromSub(session.user.sub)
}
